/**
 * Eigen URL's voor groepen en subgroepen binnen een categorie.
 *
 * **Waarom.** De drilldown liep tot nu via query-parameters: `/categorie/glasartikelen?groep=Gereedschappen`.
 * Voor een bezoeker werkt dat prima, voor Google niet: de canonical van zo'n pagina wees terug naar
 * de kale categorie, dus alle 50 groepen deelden één vindbare pagina. Zoekt iemand op
 * "gereedschap voor glas", dan was er niets specifiekers om te tonen dan het brede
 * glasartikelen-overzicht — met 700 artikelen waarvan er 191 relevant zijn.
 *
 * Nu heeft elke groep een echt adres: `/categorie/glasartikelen/gereedschappen`, met een eigen
 * titel, omschrijving en H1, en opgenomen in de sitemap. De query-vorm blijft werken en stuurt
 * door naar het nieuwe adres, zodat oude links en bladwijzers niet breken.
 *
 * **Waarom een slug en geen ruwe naam in het pad.** Groepnamen bevatten spaties, komma's en
 * ampersands ("Boren, slijpen & polijsten"). Die moeten ge-escaped in een URL, wat leidt tot
 * `%20`- en `%26`-soep die slecht deelt en waarvan Google de woorden niet los ziet. De slug is
 * leesbaar, stabiel en bevat de zoekwoorden.
 */

/**
 * Naam → URL-slug: kleine letters, accenten weg, alles wat geen letter/cijfer is wordt een streepje.
 *
 * `&` wordt "en" en `+` wordt "plus" — beide dragen betekenis in productnamen. Zou je ze zomaar
 * weggooien, dan vallen "Flamea" en "Flamea+" (twee echte, verschillende scharnierseries in
 * Douche & Bad) op dezelfde slug en wijst één adres naar twee assortimenten. De test in
 * groep-url.test.ts bewaakt dat er nergens in de catalogus twee labels op dezelfde slug landen.
 */
export function groepSlug(label) {
  return String(label ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/&/g, " en ")
    .replace(/\+/g, " plus ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * Slug → oorspronkelijke naam, gezocht binnen een lijst bekende labels.
 *
 * De slug is een versimpeling (hoofdletters en leestekens gaan verloren), dus terugrekenen kan
 * niet — we vergelijken door élk bekend label te sluggen en te matchen. Dat is O(n) over hooguit
 * enkele tientallen groepen, dus goedkoop, en het houdt de slug-regel op één plek.
 */
export function vindLabel(labels, slug) {
  const s = groepSlug(slug);
  return labels.find((l) => groepSlug(l) === s);
}

/** Pad naar een groep of subgroep binnen een categorie. */
export function groepPad(catSlug, groep, sub, sub2) {
  let p = `/categorie/${catSlug}`;
  if (groep) p += `/${groepSlug(groep)}`;
  if (groep && sub) p += `/${groepSlug(sub)}`;
  // Niveau 4 komt zelden voor en heeft geen eigen zoekvraag; die houden we als query-parameter,
  // zodat het pad maximaal drie niveaus diep blijft (categorie → groep → subgroep).
  if (groep && sub && sub2) p += `?sub2=${encodeURIComponent(sub2)}`;
  return p;
}
